import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

/** @type {Array<{canvas: string, half: string|null, shape: string}>} */
const TP_STAR_DATA = [
  {
    canvas: 'M0 46.330002h46.375586V0H0z',
    half: null,
    shape:
      'M39.533936 19.711433L13.230239 38.80065l3.838216-11.797827L7.02115 19.711433h12.418975l3.837417-11.798624 3.837418 11.798624h12.418975zM23.2785 31.510075l7.183595-1.509576 2.862114 8.800152L23.2785 31.510075z'
  },
  {
    canvas: 'M51.24816 46.330002h46.375587V0H51.248161z',
    half: 'M51.24816 46.330002h23.187793V0H51.248161z',
    shape:
      'M74.990978 31.32991L81.150908 30 84 39l-9.660206-7.202786L64.30279 39l3.895636-11.840666L58 19.841466h12.605577L74.499595 8l3.895637 11.841466H91L74.990978 31.329909z'
  },
  {
    canvas: 'M102.532209 46.330002h46.375586V0h-46.375586z',
    half: 'M102.532209 46.330002h23.187793V0h-23.187793z',
    shape:
      'M142.066994 19.711433L115.763298 38.80065l3.838215-11.797827-10.047304-7.291391h12.418975l3.837418-11.798624 3.837417 11.798624h12.418975zM125.81156 31.510075l7.183595-1.509576 2.862113 8.800152-10.045708-7.290576z'
  },
  {
    canvas: 'M153.815458 46.330002h46.375586V0h-46.375586z',
    half: 'M153.815458 46.330002h23.187793V0h-23.187793z',
    shape:
      'M193.348355 19.711433L167.045457 38.80065l3.837417-11.797827-10.047303-7.291391h12.418974l3.837418-11.798624 3.837418 11.798624h12.418974zM177.09292 31.510075l7.183595-1.509576 2.862114 8.800152-10.045709-7.290576z'
  },
  {
    canvas: 'M205.064416 46.330002h46.375587V0h-46.375587z',
    half: 'M205.064416 46.330002h23.187793V0h-23.187793z',
    shape:
      'M244.597022 19.711433l-26.3029 19.089218 3.837419-11.797827-10.047304-7.291391h12.418974l3.837418-11.798624 3.837418 11.798624h12.418975zm-16.255436 11.798642l7.183595-1.509576 2.862114 8.800152-10.045709-7.290576z'
  }
];

/** @type {Map<string, Promise<Array>>} */
const _reviewsCache = new Map();

if (!customElements.get('bbd-trustpilot-reviews-drawer')) {
  try {
    customElements.define(
      'bbd-trustpilot-reviews-drawer',
      class extends DialogComponent {
        /** @type {HTMLElement|null} */
        #trigger = null;
        /** @type {((e: MouseEvent) => void) | null} */
        #onTriggerClick = null;
        /** @type {Array|null} */
        #reviews = null;
        /** @type {AbortController|null} */
        #fetchController = null;

        #onDialogOpen = () => {
          this.refs.dialog?.classList.add('is-open');
          this.#renderDrawer();
        };

        #onDialogClose = () => {
          this.refs.dialog?.classList.remove('is-open');
        };

        connectedCallback() {
          super.connectedCallback();
          this.#fetchReviews();
          this.#bindTrigger();
          this.#bindCloseButton();
          this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
          this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
        }

        disconnectedCallback() {
          super.disconnectedCallback();
          this.#fetchController?.abort();
          this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
          this.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
          this.#unbindTrigger();
        }

        #bindTrigger() {
          const trigger = this.querySelector('.bbd-trustpilot-product-reviews__trigger');
          if (!trigger) return;

          this.#trigger = trigger;
          this.#onTriggerClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.refs.dialog?.open) return;
            this.showDialog();
          };

          this.#trigger.addEventListener('click', this.#onTriggerClick);
        }

        #bindCloseButton() {
          const closeButton = this.querySelector('.bbd-trustpilot-product-reviews__close-button');
          if (closeButton) {
            closeButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.closeDialog();
            });
          }
        }

        #unbindTrigger() {
          if (this.#trigger && this.#onTriggerClick) {
            this.#trigger.removeEventListener('click', this.#onTriggerClick);
          }
          this.#trigger = null;
          this.#onTriggerClick = null;
        }

        async #fetchReviews() {
          this.#fetchController?.abort();
          const controller = new AbortController();
          this.#fetchController = controller;

          const { businessUnitId, proxyUrl, skuList, language } = this.dataset;
          const skus = (skuList || '').split(',').filter(Boolean);

          const url = new URL(proxyUrl);
          url.searchParams.set('businessUnitId', businessUnitId);
          url.searchParams.append('stars', '4');
          url.searchParams.append('stars', '5');
          skus.forEach((sku) => url.searchParams.append('sku', sku.trim()));
          if (language) url.searchParams.set('language', language);
          url.searchParams.set('perPage', '100');

          const cacheKey = url.toString();

          if (!_reviewsCache.has(cacheKey)) {
            _reviewsCache.set(
              cacheKey,
              fetch(url.toString(), { signal: controller.signal })
                .then((res) => {
                  if (!res.ok) throw new Error(`HTTP ${res.status}`);
                  return res.json();
                })
                .then((data) => data.productReviews ?? [])
                .catch((err) => {
                  _reviewsCache.delete(cacheKey);
                  throw err;
                })
            );
          }

          try {
            this.#reviews = await _reviewsCache.get(cacheKey);
          } catch (err) {
            if (err.name === 'AbortError') return;
            // API error — log for debugging but do NOT hide the component
            console.error('[bbd-trustpilot] fetch failed:', err);
            return;
          }

          if (!this.#reviews.length) {
            this.style.display = 'none';
            return;
          }

          this.#renderMiniWidget();
        }

        #renderMiniWidget() {
          const mini = this.refs.miniWidget;
          if (!mini || !this.#reviews?.length) return;
          const count = this.#reviews.length;
          const avg = (this.#reviews.reduce((s, r) => s + r.stars, 0) / count).toFixed(1);
          const roundedStars = Math.round(Number(avg));

          // Only fetched reviews are 4-5 stars, so a rounded score below 5 means the
          // average sits under 4.5 — client wants the PDP badge fully hidden in that case.
          if (roundedStars < 5) {
            this.style.display = 'none';
            return;
          }

          const { reviewsText } = this.dataset;
          mini.innerHTML = `
            ${this.#renderStars(roundedStars)}
            <span class="bbd-trustpilot-product-reviews__mini-count text-link text-link--small">${count} ${this.#escapeHtml(reviewsText ?? '')}</span>
          `;
        }

        #renderDrawer() {
          const list = this.refs.reviewsList;
          const summaryEl = this.refs.summaryHeader;
          if (!list) return;

          const { loadingText, emptyText, reviewsText, verifiedText } = this.dataset;

          if (this.#reviews === null) {
            list.innerHTML = `<p class="bbd-trustpilot-product-reviews__loading">${this.#escapeHtml(loadingText ?? '')}</p>`;
            return;
          }

          if (!this.#reviews.length) {
            list.innerHTML = `<p class="bbd-trustpilot-product-reviews__empty">${this.#escapeHtml(emptyText ?? '')}</p>`;
            return;
          }

          // Summary header: average stars + score + Trustpilot logo
          if (summaryEl) {
            const count = this.#reviews.length;
            const avg = (this.#reviews.reduce((s, r) => s + r.stars, 0) / count).toFixed(1);
            summaryEl.innerHTML = `
              <div class="bbd-trustpilot-product-reviews__summary-rating">
                ${this.#renderStars(Math.round(Number(avg)))}
                <svg class="bbd-trustpilot-product-reviews__tp-logo" viewBox="0 0 1132.8 278.2" xmlns="http://www.w3.org/2000/svg" aria-label="Trustpilot">
                  <path d="M297.7 98.6h114.7V120h-45.1v120.3h-24.8V120h-44.9V98.6zm109.8 39.1h21.2v19.8h.4c.7-2.8 2-5.5 3.9-8.1 1.9-2.6 4.2-5.1 6.9-7.2 2.7-2.2 5.7-3.9 9-5.3 3.3-1.3 6.7-2 10.1-2 2.6 0 4.5.1 5.5.2s2 .3 3.1.4v21.8c-1.6-.3-3.2-.5-4.9-.7-1.7-.2-3.3-.3-4.9-.3-3.8 0-7.4.8-10.8 2.3-3.4 1.5-6.3 3.8-8.8 6.7-2.5 3-4.5 6.6-6 11s-2.2 9.4-2.2 15.1v48.8h-22.6V137.7zm164 102.6h-22.2V226h-.4c-2.8 5.2-6.9 9.3-12.4 12.4-5.5 3.1-11.1 4.7-16.8 4.7-13.5 0-23.3-3.3-29.3-10s-9-16.8-9-30.3v-65.1H504v62.9c0 9 1.7 15.4 5.2 19.1 3.4 3.7 8.3 5.6 14.5 5.6 4.8 0 8.7-.7 11.9-2.2 3.2-1.5 5.8-3.4 7.7-5.9 2-2.4 3.4-5.4 4.3-8.8.9-3.4 1.3-7.1 1.3-11.1v-59.5h22.6v102.5zm38.5-32.9c.7 6.6 3.2 11.2 7.5 13.9 4.4 2.6 9.6 4 15.7 4 2.1 0 4.5-.2 7.2-.5s5.3-1 7.6-1.9c2.4-.9 4.3-2.3 5.9-4.1 1.5-1.8 2.2-4.1 2.1-7-.1-2.9-1.2-5.3-3.2-7.1-2-1.9-4.5-3.3-7.6-4.5-3.1-1.1-6.6-2.1-10.6-2.9-4-.8-8-1.7-12.1-2.6-4.2-.9-8.3-2.1-12.2-3.4-3.9-1.3-7.4-3.1-10.5-5.4-3.1-2.2-5.6-5.1-7.4-8.6-1.9-3.5-2.8-7.8-2.8-13 0-5.6 1.4-10.2 4.1-14 2.7-3.8 6.2-6.8 10.3-9.1 4.2-2.3 8.8-3.9 13.9-4.9 5.1-.9 10-1.4 14.6-1.4 5.3 0 10.4.6 15.2 1.7 4.8 1.1 9.2 2.9 13.1 5.5 3.9 2.5 7.1 5.8 9.7 9.8 2.6 4 4.2 8.9 4.9 14.6h-23.6c-1.1-5.4-3.5-9.1-7.4-10.9-3.9-1.9-8.4-2.8-13.4-2.8-1.6 0-3.5.1-5.7.4-2.2.3-4.2.8-6.2 1.5-1.9.7-3.5 1.8-4.9 3.2-1.3 1.4-2 3.2-2 5.5 0 2.8 1 5 2.9 6.7 1.9 1.7 4.4 3.1 7.5 4.3 3.1 1.1 6.6 2.1 10.6 2.9 4 .8 8.1 1.7 12.3 2.6 4.1.9 8.1 2.1 12.1 3.4 4 1.3 7.5 3.1 10.6 5.4 3.1 2.3 5.6 5.1 7.5 8.5 1.9 3.4 2.9 7.7 2.9 12.7 0 6.1-1.4 11.2-4.2 15.5-2.8 4.2-6.4 7.7-10.8 10.3-4.4 2.6-9.4 4.6-14.8 5.8-5.4 1.2-10.8 1.8-16.1 1.8-6.5 0-12.5-.7-18-2.2-5.5-1.5-10.3-3.7-14.3-6.6-4-3-7.2-6.7-9.5-11.1-2.3-4.4-3.5-9.7-3.7-15.8H610zm74.6-69.7h17.1v-30.8h22.6v30.8h20.4v16.9h-20.4v54.8c0 2.4.1 4.4.3 6.2.2 1.7.7 3.2 1.4 4.4.7 1.2 1.8 2.1 3.3 2.7 1.5.6 3.4.9 6 .9 1.6 0 3.2 0 4.8-.1 1.6-.1 3.2-.3 4.8-.7v17.5c-2.5.3-5 .5-7.3.8-2.4.3-4.8.4-7.3.4-6 0-10.8-.6-14.4-1.7-3.6-1.1-6.5-2.8-8.5-5-2.1-2.2-3.4-4.9-4.2-8.2-.7-3.3-1.2-7.1-1.3-11.3v-60.5h-17.1v-17.1zm76.1 0h21.4v13.9h.4c3.2-6 7.6-10.2 13.3-12.8 5.7-2.6 11.8-3.9 18.5-3.9 8.1 0 15.1 1.4 21.1 4.3 6 2.8 11 6.7 15 11.7 4 5 6.9 10.8 8.9 17.4 2 6.6 3 13.7 3 21.2 0 6.9-.9 13.6-2.7 20-1.8 6.5-4.5 12.2-8.1 17.2-3.6 5-8.2 8.9-13.8 11.9-5.6 3-12.1 4.5-19.7 4.5-3.3 0-6.6-.3-9.9-.9-3.3-.6-6.5-1.6-9.5-2.9-3-1.3-5.9-3-8.4-5.1-2.6-2.1-4.7-4.5-6.5-7.2h-.4v51.2h-22.6V137.7zm79 51.4c0-4.6-.6-9.1-1.8-13.5-1.2-4.4-3-8.2-5.4-11.6-2.4-3.4-5.4-6.1-8.9-8.1-3.6-2-7.7-3.1-12.3-3.1-9.5 0-16.7 3.3-21.5 9.9-4.8 6.6-7.2 15.4-7.2 26.4 0 5.2.6 10 1.9 14.4 1.3 4.4 3.1 8.2 5.7 11.4 2.5 3.2 5.5 5.7 9 7.5 3.5 1.9 7.6 2.8 12.2 2.8 5.2 0 9.5-1.1 13.1-3.2 3.6-2.1 6.5-4.9 8.8-8.2 2.3-3.4 4-7.2 5-11.5.9-4.3 1.4-8.7 1.4-13.2zm39.9-90.5h22.6V120h-22.6V98.6zm0 39.1h22.6v102.6h-22.6V137.7zm42.8-39.1H945v141.7h-22.6V98.6zm91.9 144.5c-8.2 0-15.5-1.4-21.9-4.1-6.4-2.7-11.8-6.5-16.3-11.2-4.4-4.8-7.8-10.5-10.1-17.1-2.3-6.6-3.5-13.9-3.5-21.8 0-7.8 1.2-15 3.5-21.6 2.3-6.6 5.7-12.3 10.1-17.1 4.4-4.8 9.9-8.5 16.3-11.2 6.4-2.7 13.7-4.1 21.9-4.1s15.5 1.4 21.9 4.1c6.4 2.7 11.8 6.5 16.3 11.2 4.4 4.8 7.8 10.5 10.1 17.1 2.3 6.6 3.5 13.8 3.5 21.6 0 7.9-1.2 15.2-3.5 21.8-2.3 6.6-5.7 12.3-10.1 17.1-4.4 4.8-9.9 8.5-16.3 11.2-6.4 2.7-13.7 4.1-21.9 4.1zm0-17.9c5 0 9.4-1.1 13.1-3.2 3.7-2.1 6.7-4.9 9.1-8.3 2.4-3.4 4.1-7.3 5.3-11.6 1.1-4.3 1.7-8.7 1.7-13.2 0-4.4-.6-8.7-1.7-13.1s-2.9-8.2-5.3-11.6c-2.4-3.4-5.4-6.1-9.1-8.2-3.7-2.1-8.1-3.2-13.1-3.2s-9.4 1.1-13.1 3.2c-3.7 2.1-6.7 4.9-9.1 8.2-2.4 3.4-4.1 7.2-5.3 11.6-1.1 4.4-1.7 8.7-1.7 13.1 0 4.5.6 8.9 1.7 13.2 1.1 4.3 2.9 8.2 5.3 11.6 2.4 3.4 5.4 6.2 9.1 8.3 3.7 2.2 8.1 3.2 13.1 3.2zm58.4-87.5h17.1v-30.8h22.6v30.8h20.4v16.9h-20.4v54.8c0 2.4.1 4.4.3 6.2.2 1.7.7 3.2 1.4 4.4.7 1.2 1.8 2.1 3.3 2.7 1.5.6 3.4.9 6 .9 1.6 0 3.2 0 4.8-.1 1.6-.1 3.2-.3 4.8-.7v17.5c-2.5.3-5 .5-7.3.8-2.4.3-4.8.4-7.3.4-6 0-10.8-.6-14.4-1.7-3.6-1.1-6.5-2.8-8.5-5-2.1-2.2-3.4-4.9-4.2-8.2-.7-3.3-1.2-7.1-1.3-11.3v-60.5h-17.1v-17.1z" fill="#191919"/>
                  <path fill="#00b67a" d="M271.3 98.6H167.7L135.7 0l-32.1 98.6L0 98.5l83.9 61L51.8 258l83.9-60.9 83.8 60.9-32-98.5 83.8-60.9z"/>
                  <path fill="#005128" d="M194.7 181.8l-7.2-22.3-51.8 37.6z"/>
                </svg>
              </div>
              <p class="bbd-trustpilot-product-reviews__summary-text">
                <strong class="bbd-trustpilot-product-reviews__summary-score">${avg}</strong> / 5 &middot; <span class="bbd-trustpilot-product-reviews__summary-count">${count} ${this.#escapeHtml(reviewsText ?? '')}</span>
              </p>
            `;
          }

          // Review cards
          list.innerHTML = this.#reviews
            .map(
              (r) => `
            <article class="bbd-trustpilot-product-reviews__review">
              <div class="bbd-trustpilot-product-reviews__stars" aria-label="${r.stars} / 5">${this.#renderStars(r.stars)}</div>
              <div class="bbd-trustpilot-product-reviews__reviewer">
                <span class="bbd-trustpilot-product-reviews__reviewer-name">${this.#escapeHtml(r.consumer?.displayName ?? '')}</span>
                <time class="bbd-trustpilot-product-reviews__date">${this.#formatDate(r.createdAt)}</time>
              </div>
              ${r.title ? `<p class="bbd-trustpilot-product-reviews__review-title">${this.#escapeHtml(r.title)}</p>` : ''}
              <p class="bbd-trustpilot-product-reviews__review-text">${this.#escapeHtml(r.content ?? '')}</p>
              <div class="bbd-trustpilot-product-reviews__verified">
                <svg class="bbd-trustpilot-product-reviews__verified-icon" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <polyline points="3,8 6,12 13,4" stroke="#00b67a" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>${this.#escapeHtml(verifiedText ?? '')}</span>
              </div>
            </article>
          `
            )
            .join('');
        }

        /**
         * Renders the Trustpilot 5-star SVG for a given rating.
         * Colors: Math.round >= 5 → #00b67a, else → #73cf11.
         * Supports half-stars for decimal averages.
         * @param {number} count
         */
        #renderStars(count) {
          const fullCount = Math.floor(count);
          const hasHalf = count % 1 >= 0.5;
          const color = Math.round(count) >= 5 ? '#00b67a' : '#73cf11';
          const gray = '#dcdce6';

          const groups = TP_STAR_DATA.map((star, i) => {
            const isFull = i < fullCount;
            const isHalf = !isFull && i === fullCount && hasHalf;
            const canvasFill = isFull ? color : gray;
            const halfFill = isFull || isHalf ? color : gray;
            const halfPath = star.half
              ? `<path class="tp-star__canvas--half" fill="${halfFill}" d="${star.half}"></path>`
              : '';
            return `<g class="tp-star"><path class="tp-star__canvas" fill="${canvasFill}" d="${star.canvas}"></path>${halfPath}<path class="tp-star__shape" d="${star.shape}" fill="#FFF"></path></g>`;
          }).join('');

          return `<svg class="bbd-trustpilot-product-reviews__stars-svg" viewBox="0 0 251 46" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">${groups}</svg>`;
        }

        #formatDate(iso) {
          if (!iso) return '';
          const date = new Date(iso);
          if (isNaN(date.getTime())) return '';
          return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }).format(date);
        }

        #escapeHtml(str) {
          return String(str).replace(
            /[&<>"']/g,
            (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
          );
        }
      }
    );
  } catch {
    // Silently fail if custom element definition fails
  }
}
